import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, Tag, ArrowRight, Search } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { BlogGridSkeleton } from '../components/Skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_INFO = {
  'immobilienmarkt': { name: 'Immobilienmarkt', color: 'bg-blue-500', icon: '📊' },
  'tips-tricks': { name: 'Tipps und Tricks', color: 'bg-green-500', icon: '💡' },
  'bauprojekte': { name: 'Bauprojekte', color: 'bg-orange-500', icon: '🏗️' },
  'news-trends': { name: 'News & Trends', color: 'bg-purple-500', icon: '📰' }
};

// Component to load and display blog post image
const BlogPostImage = ({ postId, hasImage, title, category }) => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  useEffect(() => {
    if (hasImage && postId) {
      fetch(`${API_URL}/api/blog/posts/${postId}/image`)
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => {
          setImageData(data.image_base64);
          setLoading(false);
        })
        .catch(() => {
          setError(true);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [postId, hasImage]);
  
  if (loading) {
    return (
      <div className={`w-full h-full ${CATEGORY_INFO[category]?.color || 'bg-gray-400'} flex items-center justify-center animate-pulse`}>
        <span className="text-4xl opacity-50">{CATEGORY_INFO[category]?.icon || '📄'}</span>
      </div>
    );
  }
  
  if (imageData && !error) {
    return (
      <img 
        src={`data:image/jpeg;base64,${imageData}`}
        alt={title}
        loading="lazy"
        width="400"
        height="250"
        className="w-full h-full object-cover"
      />
    );
  }
  
  // Fallback to category color/icon
  return (
    <div className={`w-full h-full ${CATEGORY_INFO[category]?.color || 'bg-gray-400'} flex items-center justify-center`}>
      <span className="text-6xl opacity-50">{CATEGORY_INFO[category]?.icon || '📄'}</span>
    </div>
  );
};

export const Blog = () => {
  useSEO('blog');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  
  const activeCategory = searchParams.get('kategorie');

  useEffect(() => {
    fetchPosts();
  }, [activeCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const url = activeCategory 
        ? `${API_URL}/api/blog/posts/category/${activeCategory}`
        : `${API_URL}/api/blog/posts/public`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-CH', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const estimateReadTime = (content) => {
    const words = content?.split(/\s+/).length || 0;
    return Math.max(1, Math.ceil(words / 200));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#264F77] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-normal mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Blog
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Aktuelle Einblicke, Expertentipps und Trends aus der Welt der Immobilien in der Ostschweiz.
            </p>
          </div>
          
          {/* Search Bar */}
          <div className="mt-8 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Blog durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-white focus:outline-none"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSearchParams({})}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                !activeCategory 
                  ? 'bg-[#264F77] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Alle Beiträge
            </button>
            {Object.entries(CATEGORY_INFO).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => setSearchParams({ kategorie: key })}
                className={`px-4 py-2 rounded-full font-medium transition-all flex items-center gap-2 ${
                  activeCategory === key 
                    ? 'bg-[#264F77] text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span>{cat.icon}</span>
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden shadow-lg animate-pulse">
                  <div className="h-48 bg-gray-200" />
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-4" />
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-full mb-4" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {searchTerm ? 'Keine Ergebnisse gefunden' : 'Noch keine Blogbeiträge'}
              </h3>
              <p className="text-gray-500">
                {searchTerm 
                  ? 'Versuchen Sie es mit einem anderen Suchbegriff.'
                  : 'Schauen Sie bald wieder vorbei für spannende Inhalte!'
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <article 
                  key={post.id} 
                  className={`bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                    index === 0 && !activeCategory ? 'md:col-span-2 lg:col-span-2' : ''
                  }`}
                >
                  {/* Image */}
                  <div className={`relative overflow-hidden ${index === 0 && !activeCategory ? 'h-64' : 'h-48'}`}>
                    <BlogPostImage 
                      postId={post.id}
                      hasImage={post.has_image}
                      title={post.title}
                      category={post.category}
                    />
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 rounded-full text-white text-sm font-medium ${CATEGORY_INFO[post.category]?.color || 'bg-gray-500'}`}>
                        {CATEGORY_INFO[post.category]?.name || post.category}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    {/* Meta */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(post.published_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {estimateReadTime(post.content)} Min.
                      </span>
                    </div>
                    
                    {/* Title */}
                    <h2 className={`font-bold text-gray-900 mb-3 line-clamp-2 ${
                      index === 0 && !activeCategory ? 'text-2xl' : 'text-xl'
                    }`}>
                      {post.title}
                    </h2>
                    
                    {/* Excerpt */}
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    
                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                            <Tag size={12} />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Read More Link */}
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-[#264F77] font-semibold hover:gap-3 transition-all"
                    >
                      Weiterlesen
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="bg-[#264F77] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Bleiben Sie informiert</h2>
          <p className="text-lg text-gray-300 mb-8">
            Kontaktieren Sie uns für weitere Informationen zu unseren Projekten und Dienstleistungen.
          </p>
          <Link 
            to="/kontakt"
            className="inline-flex items-center gap-2 bg-white text-[#264F77] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Jetzt Immobilie bewerten lassen
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};
